import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { requireSession } from '@/lib/auth';
import { invalidateProductRelatedQueries } from '@/lib/queryInvalidation';
import { TOAST_MESSAGES } from '@/lib/toastMessages';

export type SyncType = 'products' | 'categories' | 'orders' | 'menus' | 'media';

export interface SyncJob {
  id: string;
  store_id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  sync_type: string;
  current_page: number;
  total_pages: number | null;
  products_processed: number;
  products_total: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  last_checkpoint_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SyncContextValue {
  // State
  isSyncing: boolean;
  syncType: SyncType | null;
  progress: number;
  isInitializing: boolean;
  activeJobId: string | null;
  currentOperation: string | null;
  itemsProcessed: number;
  itemsTotal: number | null;
  elapsedTime: string;
  
  // Actions
  startSync: (type: SyncType, storeId: string) => Promise<string | null>;
  cancelSync: () => void;
  
  // Event callbacks
  registerOnComplete: (callback: (job: SyncJob) => void) => () => void;
  registerOnError: (callback: (error: string) => void) => () => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

const SUPABASE_URL = "https://zmktubbymvbykygjjeis.supabase.co";

// Map sync types to their edge functions
const SYNC_ENDPOINTS: Record<SyncType, { endpoint: string; bodyKey?: string }> = {
  products: { endpoint: 'woo-sync-products' },
  categories: { endpoint: 'woo-sync-products', bodyKey: 'sync_categories' },
  orders: { endpoint: 'woo-sync-orders' },
  menus: { endpoint: 'woo-sync-menus' },
  media: { endpoint: 'woo-sync-media' },
};

// Sync types that use the sync_jobs table for progress tracking
const JOB_TRACKED_SYNCS: SyncType[] = ['products', 'categories'];

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  
  // Core state
  const [activeSyncType, setActiveSyncType] = useState<SyncType | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState('');
  
  // For fast syncs (orders, menus) that don't use job table
  const [fastSyncInProgress, setFastSyncInProgress] = useState(false);
  
  // Callbacks
  const onCompleteCallbacks = useRef<Set<(job: SyncJob) => void>>(new Set());
  const onErrorCallbacks = useRef<Set<(error: string) => void>>(new Set());

  // Poll for job status when we have an active job
  const { data: activeJob } = useQuery({
    queryKey: ['sync-job-global', activeStoreId, activeJobId],
    queryFn: async () => {
      if (!activeStoreId) return null;

      // If we have an active job ID, fetch that specific job
      if (activeJobId) {
        const { data, error } = await supabase
          .from('sync_jobs')
          .select('*')
          .eq('id', activeJobId)
          .single();
        
        if (error) throw error;
        return data as SyncJob;
      }

      // Otherwise, check for any active job for this store
      const { data, error } = await supabase
        .from('sync_jobs')
        .select('*')
        .eq('store_id', activeStoreId)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) throw error;
      return data as SyncJob | null;
    },
    enabled: !!activeStoreId && !!activeJobId && activeSyncType !== null && JOB_TRACKED_SYNCS.includes(activeSyncType),
    refetchInterval: (query) => {
      const job = query.state.data;
      if (job && (job.status === 'pending' || job.status === 'processing')) {
        // Poll faster in the first 15 seconds
        const isInitialPhase = syncStartTime && (Date.now() - syncStartTime) < 15000;
        return isInitialPhase ? 1000 : 2000;
      }
      return false;
    },
  });

  // Update elapsed time
  useEffect(() => {
    if (!syncStartTime) {
      setElapsedTime('');
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Date.now() - syncStartTime;
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      if (minutes > 0) {
        setElapsedTime(`${minutes}m ${seconds % 60}s`);
      } else {
        setElapsedTime(`${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [syncStartTime]);

  // Handle job completion/failure
  useEffect(() => {
    if (!activeJob) return;

    if (activeJob.status === 'completed') {
      onCompleteCallbacks.current.forEach(cb => cb(activeJob));
      resetSyncState();
      invalidateProductRelatedQueries(queryClient);
    } else if (activeJob.status === 'failed') {
      const errorMsg = activeJob.error_message || 'Sync failed';
      onErrorCallbacks.current.forEach(cb => cb(errorMsg));
      resetSyncState();
    } else if (activeJob.status === 'cancelled') {
      resetSyncState();
    }
  }, [activeJob?.status, activeJob?.id, queryClient]);

  const resetSyncState = useCallback(() => {
    setActiveSyncType(null);
    setActiveJobId(null);
    setActiveStoreId(null);
    setSyncStartTime(null);
    setFastSyncInProgress(false);
  }, []);

  // Start sync
  const startSync = useCallback(async (type: SyncType, storeId: string): Promise<string | null> => {
    // Prevent starting if already syncing
    if (activeSyncType !== null) {
      toast.info(`${activeSyncType} sync in progress`, {
        description: 'Please wait for the current sync to complete.',
      });
      return null;
    }

    try {
      const session = await requireSession();
      const config = SYNC_ENDPOINTS[type];
      
      const body: Record<string, any> = { store_id: storeId };
      if (config.bodyKey) {
        body[config.bodyKey] = true;
      }

      // Set state before making request
      setActiveSyncType(type);
      setActiveStoreId(storeId);
      setSyncStartTime(Date.now());

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${config.endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 409 - sync already in progress
        if (response.status === 409 && data.job_id) {
          setActiveJobId(data.job_id);
          toast.info('Sync already in progress', {
            description: 'Tracking existing sync job...',
          });
          return data.job_id;
        }
        throw new Error(data.error || data.message || 'Failed to start sync');
      }

      // For job-tracked syncs
      if (JOB_TRACKED_SYNCS.includes(type) && data.job_id) {
        setActiveJobId(data.job_id);
        toast.info(TOAST_MESSAGES.SYNC_STARTED, { 
          description: TOAST_MESSAGES.SYNC_IN_PROGRESS 
        });
        return data.job_id;
      }

      // For fast syncs (orders, menus, media), handle immediate completion
      setFastSyncInProgress(true);
      
      // Invalidate related queries
      if (type === 'orders') {
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['order-stats-summary'] });
        toast.success(`Synced ${data.orders_stored || 0} orders`);
      } else if (type === 'menus') {
        queryClient.invalidateQueries({ queryKey: ['store-menus'] });
        queryClient.invalidateQueries({ queryKey: ['store-menu-items'] });
        toast.success(`Synced ${data.menus_synced || 0} menus`);
      } else if (type === 'media') {
        queryClient.invalidateQueries({ queryKey: ['store-media'] });
        toast.success(`Synced ${data.synced_count || 0} media items`);
      }

      resetSyncState();
      return null;
    } catch (error: any) {
      const message = error.message || 'Sync failed';
      toast.error(TOAST_MESSAGES.SYNC_FAILED(message));
      onErrorCallbacks.current.forEach(cb => cb(message));
      resetSyncState();
      return null;
    }
  }, [activeSyncType, queryClient, resetSyncState]);

  // Cancel sync
  const cancelSync = useCallback(async () => {
    if (!activeJobId) {
      resetSyncState();
      return;
    }

    try {
      const { error } = await supabase
        .from('sync_jobs')
        .update({ status: 'cancelled' })
        .eq('id', activeJobId);

      if (error) throw error;
      
      toast.info(TOAST_MESSAGES.SYNC_CANCELLED);
      resetSyncState();
      queryClient.invalidateQueries({ queryKey: ['sync-job-global'] });
    } catch (error: any) {
      toast.error(TOAST_MESSAGES.ERROR_GENERIC(error.message));
    }
  }, [activeJobId, queryClient, resetSyncState]);

  // Register callbacks
  const registerOnComplete = useCallback((callback: (job: SyncJob) => void) => {
    onCompleteCallbacks.current.add(callback);
    return () => {
      onCompleteCallbacks.current.delete(callback);
    };
  }, []);

  const registerOnError = useCallback((callback: (error: string) => void) => {
    onErrorCallbacks.current.add(callback);
    return () => {
      onErrorCallbacks.current.delete(callback);
    };
  }, []);

  // Calculate progress
  const getProgress = useCallback(() => {
    if (!activeJob) return 0;
    if (activeJob.status === 'completed') return 100;
    if (!activeJob.products_total || activeJob.products_total === 0) return 0;
    return Math.round((activeJob.products_processed / activeJob.products_total) * 100);
  }, [activeJob]);

  // Check if we're in the initial connecting phase
  const isInitializing = (activeSyncType !== null && JOB_TRACKED_SYNCS.includes(activeSyncType)) && 
    (!activeJob || (activeJob.status === 'pending' || activeJob.status === 'processing') && 
    (!activeJob.products_total || activeJob.products_total === 0));

  // Get operation message
  const getOperationMessage = () => {
    if (!activeSyncType) return null;
    if (isInitializing) return `Connecting to store...`;
    if (activeJob) {
      return `Syncing ${activeSyncType}...`;
    }
    if (fastSyncInProgress) return `Syncing ${activeSyncType}...`;
    return null;
  };

  const value: SyncContextValue = {
    isSyncing: activeSyncType !== null,
    syncType: activeSyncType,
    progress: getProgress(),
    isInitializing,
    activeJobId,
    currentOperation: getOperationMessage(),
    itemsProcessed: activeJob?.products_processed || 0,
    itemsTotal: activeJob?.products_total || null,
    elapsedTime,
    startSync,
    cancelSync,
    registerOnComplete,
    registerOnError,
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}
